import { anchorDay, cacheSavings, cutoff, selectRange, spendByHarness } from "./aggregate";
import type { LedgerGap, LedgerSession } from "./api";

/**
 * Everything the overview page shows, derived from the two ledger endpoints and
 * the URL filters. One pass: the hero, strip, chart, top repos and table all
 * read from the same filtered set, so a filter can never update one section and
 * miss another.
 */

export const RANGE_OPTIONS = ["7d", "30d", "90d", "all"] as const;
export type RangeKey = (typeof RANGE_OPTIONS)[number];
const RANGE_DAYS: Record<RangeKey, number> = { "7d": 7, "30d": 30, "90d": 90, all: 0 };

export type LedgerSearch = {
	range?: RangeKey;
	harness?: string;
	/** Comma-joined repo names. Absent = every repo. */
	repo?: string;
	q?: string;
	/** Sessions-table sort. Absent = cost. */
	sort?: SortKey;
	/** Absent = the sort key's natural direction (`defaultDir`). */
	dir?: SortDir;
	/** Sessions-table page, 1-based. Absent = 1. */
	page?: number;
};

export const SORT_KEYS = [
	"first",
	"title",
	"repo",
	"harness",
	"host",
	"userPrompts",
	"activeMin",
	"cost",
] as const;
export type SortKey = (typeof SORT_KEYS)[number];
export type SortDir = "asc" | "desc";
export const DEFAULT_SORT: SortKey = "cost";

/** Text columns read A→Z first; numbers read biggest first. */
export function defaultDir(key: SortKey): SortDir {
	return key === "title" || key === "repo" || key === "host" || key === "harness" ? "asc" : "desc";
}

function positiveInt(value: unknown): number | undefined {
	const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
	return Number.isInteger(n) && n >= 1 ? n : undefined;
}

export function parseLedgerSearch(search: Record<string, unknown>): LedgerSearch {
	const out: LedgerSearch = {};
	if (
		typeof search.range === "string" &&
		(RANGE_OPTIONS as readonly string[]).includes(search.range)
	)
		out.range = search.range as RangeKey;
	if (typeof search.harness === "string" && search.harness) out.harness = search.harness;
	if (typeof search.repo === "string" && search.repo) out.repo = search.repo;
	if (typeof search.q === "string" && search.q) out.q = search.q;
	if (typeof search.sort === "string" && (SORT_KEYS as readonly string[]).includes(search.sort))
		out.sort = search.sort as SortKey;
	if (search.dir === "asc" || search.dir === "desc") out.dir = search.dir;
	const page = positiveInt(search.page);
	if (page) out.page = page;
	return out;
}

/**
 * Validate, then drop every default so the plain view stays `/ledger`: range
 * `all`, sort `cost`, the sort's natural direction and page 1.
 */
export function cleanLedgerSearch(search: LedgerSearch): LedgerSearch {
	const out = parseLedgerSearch(search as Record<string, unknown>);
	if (out.range === "all") delete out.range;
	if (out.sort === DEFAULT_SORT) delete out.sort;
	if (out.dir === defaultDir(out.sort ?? DEFAULT_SORT)) delete out.dir;
	if (out.page === 1) delete out.page;
	return out;
}

export function repoList(search: LedgerSearch): string[] {
	return (search.repo ?? "").split(",").filter(Boolean);
}

/**
 * Harness colors are categories, not the brand. Claude Code rides the accent;
 * the rest are fixed hues. Codex uses the off-accent token so it never matches
 * the active accent (lavender under ember, teal under lavender).
 */
export const HARNESS_COLOR: Record<string, string> = {
	claude: "var(--accent)",
	"claude-other": "var(--series-claude-other)",
	grok: "var(--color-ink-muted)",
	pi: "var(--color-ink-subtle)",
	codex: "var(--series-off-accent)",
	zcode: "var(--color-chart-6)",
	hermes: "var(--color-info)",
	cursor: "var(--color-chart-4)",
};

/**
 * Pseudo-harness for Claude Code spend on non-Anthropic models (GLM, Gemini,
 * Qwen… routed through Claude Code). It only exists in the hero's harness
 * breakdown; no session carries it, so the filter, daily chart and table never
 * see it.
 */
export const CLAUDE_OTHER = "claude-other";

/**
 * A Claude Code session's cost on non-Anthropic models: the `modelCost` keys
 * that don't start with `claude-` (keys are already normalized, so
 * `anthropic/claude-*` is stored as `claude-*`). 0 for every other harness.
 * Clamped to [0, cost] so float drift can't push Claude Code negative.
 */
export function otherModelCost(session: LedgerSession): number {
	if (session.harness !== "claude") return 0;
	let other = 0;
	for (const [model, cost] of Object.entries(session.modelCost)) {
		if (!model.startsWith("claude-")) other += cost;
	}
	return Math.min(Math.max(other, 0), session.cost);
}

type HarnessRow = ReturnType<typeof spendByHarness>[number];

/**
 * Below this many dollars a remainder is float residue, not spend (a
 * hundredth of a hundredth of a cent). `0.9 − (0.7 + 0.2)` is 1.1e-16, which
 * would otherwise keep a "$0.00" Claude Code row at the bar's minimum width.
 */
const ZERO_USD = 1e-6;

/**
 * Move Claude Code's non-Anthropic spend out of the `claude` row into a
 * `claude-other` row. Claude keeps `cost − other`, so cost not attributed to
 * any model stays with Claude Code and the total is unchanged by construction.
 * No row is added when there is no such spend, and the Claude row is dropped if
 * it ends at 0 (within `ZERO_USD`), so neither leaves a zero-width sliver in
 * the bar.
 */
function splitClaudeOther(rows: HarnessRow[], sessions: LedgerSession[]): HarnessRow[] {
	let cost = 0;
	let count = 0;
	let whollyOther = 0;
	let activeMin = 0;
	for (const session of sessions) {
		const other = otherModelCost(session);
		if (other <= 0) continue;
		cost += other;
		count += 1;
		if (other >= session.cost) whollyOther += 1;
		// Transcripts don't say which model was active each minute, so active
		// time is apportioned by cost share. It's an estimate that keeps the
		// two cards summing to the session's active time.
		activeMin += session.activeMin * (other / session.cost);
	}
	const claude = rows.find((row) => row.harness === "claude");
	if (!claude || cost <= 0) return rows;

	// A session mixing Anthropic and other models counts in both rows; only a
	// session entirely on other models leaves the Claude Code count.
	const restCost = claude.cost - cost;
	const rest: HarnessRow = {
		...claude,
		cost: Math.abs(restCost) < ZERO_USD ? 0 : restCost,
		sessions: claude.sessions - whollyOther,
		activeMin: claude.activeMin - activeMin,
	};
	const other: HarnessRow = {
		harness: CLAUDE_OTHER,
		cost,
		sessions: count,
		activeMin,
		basis: "computed",
	};
	return [...rows.filter((row) => row !== claude), ...(rest.cost > 0 ? [rest] : []), other].sort(
		(a, b) => b.cost - a.cost,
	);
}

export function harnessColor(harness: string): string {
	return HARNESS_COLOR[harness] ?? "var(--color-ink-tertiary)";
}

/** Case-insensitive match on title, repo, id, host and branch. */
export function matchesQuery(session: LedgerSession, q: string): boolean {
	const needle = q.trim().toLowerCase();
	if (!needle) return true;
	return [session.title, session.repo, session.id, session.host, session.branch].some((field) =>
		(field ?? "").toLowerCase().includes(needle),
	);
}

type Filters = { from: string; harness?: string; repos: string[]; q: string };

function filterSessions(
	sessions: LedgerSession[],
	filters: Filters,
	options: { ignoreRepos?: boolean } = {},
): LedgerSession[] {
	const repos = new Set(
		options.ignoreRepos || filters.repos.length === 0 ? sessions.map((s) => s.repo) : filters.repos,
	);
	const harnesses = new Set(filters.harness ? [filters.harness] : sessions.map((s) => s.harness));
	const matching = sessions.filter((s) => matchesQuery(s, filters.q));
	return selectRange(matching, filters.from, "9999", repos, harnesses);
}

const DAY_MS = 86_400_000;

function addDays(day: string, n: number): string {
	return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

export type DailyPoint = {
	day: string;
	total: number;
	sessions: number;
	byHarness: Record<string, number>;
};

/**
 * Cost per UTC day, stacked by harness. Every day in [start, end] is present
 * (zero-filled) so bar position is proportional to time.
 */
export function dailySeries(sessions: LedgerSession[], start: string, end: string): DailyPoint[] {
	const byDay = new Map<string, DailyPoint>();
	for (const session of sessions) {
		for (const [day, cost] of Object.entries(session.days)) {
			const point = byDay.get(day) ?? { day, total: 0, sessions: 0, byHarness: {} };
			point.total += cost;
			point.sessions += 1;
			point.byHarness[session.harness] = (point.byHarness[session.harness] ?? 0) + cost;
			byDay.set(day, point);
		}
	}
	if (!start || !end || start > end) return [];
	const out: DailyPoint[] = [];
	for (let day = start; day <= end; day = addDays(day, 1)) {
		out.push(byDay.get(day) ?? { day, total: 0, sessions: 0, byHarness: {} });
	}
	return out;
}

export type RepoSpend = { repo: string; spend: number; sessions: number };

export function spendByRepo(sessions: LedgerSession[]): RepoSpend[] {
	const rows = new Map<string, RepoSpend>();
	for (const session of sessions) {
		const row = rows.get(session.repo) ?? { repo: session.repo, spend: 0, sessions: 0 };
		row.spend += session.cost;
		row.sessions += 1;
		rows.set(session.repo, row);
	}
	return [...rows.values()].sort((a, b) => b.spend - a.spend || a.repo.localeCompare(b.repo));
}

export function buildOverview(sessions: LedgerSession[], search: LedgerSearch, now = new Date()) {
	const anchor = anchorDay(sessions, now);
	const range = search.range ?? "all";
	const filters: Filters = {
		from: cutoff(anchor, RANGE_DAYS[range]),
		harness: search.harness,
		repos: repoList(search),
		q: search.q ?? "",
	};
	const visible = filterSessions(sessions, filters);
	// The repo dropdown lists what every other filter leaves, so its spend
	// figures match what picking a repo would show.
	const repoOptions = spendByRepo(filterSessions(sessions, filters, { ignoreRepos: true }));

	const total = visible.reduce((sum, s) => sum + s.cost, 0);
	const harnesses = splitClaudeOther(spendByHarness(visible), visible).map((row) => ({
		...row,
		share: total ? row.cost / total : 0,
	}));
	const bases = new Set(harnesses.map((row) => row.basis));

	const days = visible.flatMap((s) => Object.keys(s.days)).sort();
	const firstDay = range === "all" ? days[0] : filters.from;
	const daily = dailySeries(visible, firstDay ?? "", anchor);

	const monthStart = `${anchor.slice(0, 7)}-01`;
	// A short range can start after the 1st; the strip labels the window it sums.
	const monthFrom = filters.from > monthStart ? filters.from : monthStart;
	const monthCost = visible.reduce(
		(sum, s) =>
			sum +
			Object.entries(s.days).reduce(
				(acc, [day, cost]) => (day >= monthStart ? acc + cost : acc),
				0,
			),
		0,
	);
	const prompts = visible.reduce((sum, s) => sum + s.userPrompts, 0);
	const activeMin = visible.reduce((sum, s) => sum + s.activeMin, 0);

	return {
		anchor,
		range,
		visible,
		total,
		harnesses,
		mixed: bases.size > 1,
		daily,
		firstDay: days[0] ?? null,
		lastDay: days.at(-1) ?? null,
		repos: spendByRepo(visible),
		repoOptions,
		monthStart,
		monthFrom,
		monthCost,
		prompts,
		costPerPrompt: prompts ? total / prompts : 0,
		activeMin,
		costPerActiveHour: activeMin ? total / (activeMin / 60) : 0,
		savings: cacheSavings(visible),
		hasClaude: visible.some((s) => s.harness === "claude"),
		top: visible.reduce<LedgerSession | undefined>(
			(best, s) => (!best || s.cost > best.cost ? s : best),
			undefined,
		),
	};
}

export type Overview = ReturnType<typeof buildOverview>;

/** Harness keys present in the unfiltered data, highest spend first (the segmented control). */
export function harnessOptions(sessions: LedgerSession[]): string[] {
	return spendByHarness(sessions).map((row) => row.harness);
}

function compareSessions(a: LedgerSession, b: LedgerSession, key: SortKey): number {
	const left = a[key];
	const right = b[key];
	if (typeof left === "string" || typeof right === "string")
		return String(left ?? "").localeCompare(String(right ?? ""));
	return (Number(left) || 0) - (Number(right) || 0);
}

/** The sessions table order. `dir` is 1 ascending, -1 descending. Returns a copy. */
export function sortSessions(
	sessions: LedgerSession[],
	key: SortKey,
	dir: 1 | -1,
): LedgerSession[] {
	return [...sessions].sort((a, b) => dir * compareSessions(a, b, key));
}

export type GapKind = "no-parser" | "unpriced" | "empty";
export type GapRow = {
	key: string;
	kind: GapKind;
	path: string;
	/** Harness of the first gap seen for the file (a file belongs to one harness). */
	harness: string;
	turns: number;
	models: string[];
};

const GAP_KIND: Record<string, GapKind> = {
	no_parser: "no-parser",
	unpriced: "unpriced",
	empty: "empty",
};

/**
 * One row per excluded file. Unpriced gaps are stored one per turn; they fold
 * into their file with a turn count and the models that had no price.
 */
export function groupGaps(gaps: LedgerGap[]): {
	rows: GapRow[];
	noParser: number;
	unpricedTurns: number;
	empty: number;
} {
	const rows = new Map<string, GapRow>();
	let noParser = 0;
	let unpricedTurns = 0;
	let empty = 0;
	for (const gap of gaps) {
		const kind = GAP_KIND[gap.reason] ?? "empty";
		if (kind === "no-parser") noParser += 1;
		else if (kind === "unpriced") unpricedTurns += 1;
		else empty += 1;
		const path = gap.objectKey ?? `${gap.harness} session ${gap.sessionId ?? gap.id}`;
		const key = `${kind}:${path}`;
		const row = rows.get(key) ?? { key, kind, path, harness: gap.harness, turns: 0, models: [] };
		row.turns += 1;
		if (gap.model && !row.models.includes(gap.model)) row.models.push(gap.model);
		rows.set(key, row);
	}
	const order: Record<GapKind, number> = { "no-parser": 0, unpriced: 1, empty: 2 };
	return {
		rows: [...rows.values()].sort(
			(a, b) => order[a.kind] - order[b.kind] || b.turns - a.turns || a.path.localeCompare(b.path),
		),
		noParser,
		unpricedTurns,
		empty,
	};
}

/** CSV with a header row. Quotes every field that needs it. */
export function toCsv(header: string[], rows: (string | number | null)[][]): string {
	const cell = (value: string | number | null) => {
		// A leading = + - @ makes a spreadsheet evaluate the cell. Session titles
		// come from transcripts, so neutralize them (CWE-1236).
		const raw = value == null ? "" : String(value);
		const text = typeof value === "string" && /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
		return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
	};
	return [header, ...rows].map((row) => row.map(cell).join(",")).join("\n");
}
